import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import entityComposition from '../common/classes/entityComposition';
import FormEditor from './formEditor';
import routeHelper from '../common/classes/routeHelper';
import { formTypePermissionSelector } from '../common/classes/permissions';
import { evaluateSource } from '../../../behaviors/src/lib/evaluateSource';
import { nativeBehaviorEvaluator } from '../../nativeBehaviors/nativeBehaviorEvaluator';
import { configBehaviorsFunc, ensureConfigBehaviorsReady } from '../../nativeBehaviors/configBehaviors';
const DEFAULT_VALIDATION_MESSAGE = 'This form cannot be saved. Check required fields.';

/**
 * Evaluate the format-level `validation` behavior for formats that opt in via
 * `validateOnWebSave`. Returns an error message, or null when the save may proceed.
 */
const evaluateFormatValidation = async (format, content) => {
  const {
    validateOnWebSave,
    validation
  } = format || {};
  if (!validateOnWebSave || validation === undefined) {
    return null;
  }
  await ensureConfigBehaviorsReady();
  const nativeFunctions = {
    errorHandler: err => {
      throw err;
    },
    evaluator: nativeBehaviorEvaluator.bind(null, {}, 'form'),
    configBehaviors: configBehaviorsFunc
  };
  const result = await evaluateSource(validation, content, '', {}, nativeFunctions);
  if (typeof result === 'string' && result.length > 0) {
    return result;
  }
  return result === false ? DEFAULT_VALIDATION_MESSAGE : null;
};
const FormEditorContainer = ({
  canView,
  location,
  form: {
    active,
    active: {
      content: {
        title
      } = {}
    } = {}
  } = {
    active: {
      isNew: false
    }
  },
  match: {
    params: {
      cabinetIdentifier,
      formTypeIdentifier,
      id
    }
  }
}) => {
  // Redirect and post authorization error message if permissions insufficient
  useEffect(() => {
    routeHelper.handleViewRedirect(canView, location);
  }, [canView, location]);

  // define an instance variable so that useCallback methods have the latest data
  const activeRef = useRef();
  useEffect(() => {
    activeRef.current = active;
  }, [active]);
  const validate = useCallback(async () => {
    const errors = [];
    const {
      current: {
        content = {},
        format
      } = {}
    } = activeRef;

    // special casing for handling hull title validation. Can be removed when behaviors validation
    // is added: https://www.pivotaltracker.com/story/show/182516842
    if (!cabinetIdentifier && formTypeIdentifier && content.title === undefined) {
      errors.push('You must provide a title before saving. (See General Section)');
    }
    const formatValidationError = await evaluateFormatValidation(format, content);
    if (formatValidationError) {
      errors.push(formatValidationError);
    }
    return errors;
  }, [cabinetIdentifier, formTypeIdentifier]);
  const handleBack = () => {
    if (cabinetIdentifier) {
      return routeHelper.updateURL(`/cabinets/${cabinetIdentifier}`);
    }
    return routeHelper.updateURL(`/forms/${formTypeIdentifier}`);
  };

  // jobs use "related" to pass in job operations to call. These remaining form items could probably
  // be cleaned up and put in formEditor directly
  const related = {
    onBack: handleBack,
    onValidate: validate
  };
  return /*#__PURE__*/React.createElement(FormEditor, {
    id: id,
    formTitle: title,
    related: related,
    sectionsLabel: "Sections"
  });
};
FormEditorContainer.propTypes = {
  canView: PropTypes.bool.isRequired,
  form: PropTypes.shape({
    active: PropTypes.shape({
      isNew: PropTypes.bool,
      id: PropTypes.string,
      assets: PropTypes.arrayOf(PropTypes.shape({})),
      jobNumber: PropTypes.string,
      documents: PropTypes.arrayOf(PropTypes.shape({})),
      phaseCodes: PropTypes.arrayOf(PropTypes.shape({})),
      piecesOther: PropTypes.arrayOf(PropTypes.shape({})),
      flangeValveInspection: PropTypes.arrayOf(PropTypes.shape({})),
      flangeValve: PropTypes.arrayOf(PropTypes.shape({}))
    }),
    loading: PropTypes.bool.isRequired,
    pendingChanges: PropTypes.bool.isRequired
  }),
  formActions: PropTypes.shape({
    serverSave: PropTypes.func,
    serverShow: PropTypes.func,
    showCancel: PropTypes.func,
    change: PropTypes.func
  }),
  location: PropTypes.shape({}).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      cabinetIdentifier: PropTypes.string,
      formTypeIdentifier: PropTypes.string,
      id: PropTypes.string
    }),
    url: PropTypes.string
  }).isRequired
};
export { FormEditorContainer as TestFormEditorContainer };
export default entityComposition(FormEditorContainer, ['form'], {
  canView: formTypePermissionSelector('view_detail')
}, []);